import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../images/logo.jpg';

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;


const headers_obj = {
    headers: {
        'ZCFKEY': apiKey
    }
}
// --- API Helper ---
export const fetchzcql = async (sql) => {
    try {
        const res = await axios.post(`${apiUrl}zcql`, { sql }, {
            headers: { Accept: 'application/json', 'ZCFKEY': apiKey }
        });
        return res.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        return null;
    }
};

function BookingTracker() {
    // --- States ---
    const [inputValue, setInputValue] = useState('');
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    // View Management: null means search/list view, a ROWID means details view
    const [activeBookingId, setActiveBookingId] = useState(null);

    // --- Search Logic ---
    const handleSearch = async (e) => {
        e.preventDefault();
        if (!inputValue) return;

        setLoading(true);
        const sql = `SELECT ROWID, customer_name, eventType, booking_date, status, customer_email, customer_phone 
                     FROM bookings 
                     WHERE customer_email = '${inputValue}' OR customer_phone = '${inputValue}'  OR ROWID = '${inputValue}'
                     ORDER BY booking_date DESC`;

        const result = await fetchzcql(sql);

        if (result && result.success && result.response) {
            const formattedData = result.response.map(item => item.bookings);
            setBookings(formattedData);
        } else {
            setBookings([]);
        }
        setSearched(true);
        setLoading(false);
    };

    const getStatusStyle = (status) => {
        const base = { padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' };
        const s = status?.toLowerCase();

        switch (s) {
            case 'booked':
            case 'confirmed':
                return { ...base, backgroundColor: '#E6FFFA', color: '#319795' };
            case 'pending':
                return { ...base, backgroundColor: '#FFFBEB', color: '#B45309' };
            case 'cancelled':
                return { ...base, backgroundColor: '#FFF5F5', color: '#E53E3E' };
            default:
                return { ...base, backgroundColor: '#EDF2F7', color: '#4A5568' };
        }
    };

    // --- Sub-Component: Details View ---
    const RenderDetails = ({ id, onBack }) => {
        const [booking, setBooking] = useState(null);
        const [timeSlotLabel, setTimeSlotLabel] = useState("");
        const [detailLoading, setDetailLoading] = useState(true);

        useEffect(() => {
            const getFullDetails = async () => {
                try {
                    const sql = `SELECT * FROM bookings WHERE ROWID = '${id}'`;
                    const result = await fetchzcql(sql);
                    if (result?.success && result.response?.length > 0) {
                        const data = result.response[0].bookings;
                        setBooking(data);

                        const slotId = data.time_slot_id || data.time_slot;
                        if (slotId) {
                            const slotSql = `SELECT label FROM time_slots WHERE ROWID = '${slotId}'`;
                            const slotRes = await fetchzcql(slotSql);
                            if (slotRes?.success && slotRes.response?.length > 0) {
                                setTimeSlotLabel(slotRes.response[0]['time_slots']['label']);
                            }
                        }
                    }
                } finally {
                    setDetailLoading(false);
                }
            };
            getFullDetails();
        }, [id]);

        if (detailLoading) return (
            <div style={styles.centerContainer}>
                <div style={styles.loader}></div>
                <p style={{ color: '#72398E', marginTop: '10px', fontWeight: '600' }}>Fetching Details...</p>
            </div>
        );

        if (!booking) return (
            <div style={styles.centerContainer}>
                <p>Booking details not found.</p>
                <button onClick={onBack} style={styles.primaryButton}>Back</button>
            </div>
        );

        return (
            <div style={styles.container}>
                <button onClick={onBack} style={styles.backBtn}>← Back to My Bookings</button>
                <div style={styles.card}>
                    <div style={styles.detailHeader}>
                        <div>
                            <h1 style={styles.detailTitle}>Booking Summary</h1>
                            <p style={styles.idLabel}>ID: {booking.ROWID}</p>
                        </div>
                        <span style={getStatusStyle(booking.status)}>{booking.status || 'Processed'}</span>
                    </div>

                    <div style={styles.divider} />

                    <div style={styles.section}>
                        <span style={styles.miniLabel}>Customer Details</span>
                        <div style={styles.infoValue}>{booking.customer_name}</div>
                        <div style={styles.subText}>{booking.customer_email}</div>
                        <div style={styles.subText}>{booking.customer_phone}</div>
                    </div>

                    <div style={styles.infoGrid}>
                        <div>
                            <span style={styles.miniLabel}>Event Type</span>
                            <div style={styles.infoValue}>{booking.eventType}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <span style={styles.miniLabel}>Date</span>
                            <div style={styles.infoValue}>
                                {new Date(booking.booking_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                        </div>
                    </div>

                    <div style={styles.timeSlotBox}>
                        <span style={styles.miniLabel}>Scheduled Time Slot</span>
                        <div style={styles.timeValue}>⏰ {timeSlotLabel || "Confirmed Slot"}</div>
                    </div>

                    <div style={styles.footer}>
                        <p style={styles.footerNote}>Please show this summary at the venue.</p>
                        <button style={styles.printButton} onClick={() => window.print()}>Print Summary</button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Main View Toggle ---
    return (
        <div style={activeBookingId || bookings.length > 0 ? styles.page : styles.centeredPage}>
            <div style={styles.container}>

                {/* Logo Section - Center Aligned */}
                <div style={styles.logoWrapper}>
                    <img src={logo} alt="Logo" style={styles.logo} />
                </div>

                {!activeBookingId ? (
                    <>
                        <div style={styles.header}>
                            <h1 style={styles.title}>Track Your Bookings</h1>
                            <p style={styles.subtitle}>Enter your email or mobile number</p>
                        </div>

                        <form onSubmit={handleSearch} style={styles.searchBox}>
                            <input
                                style={styles.input}
                                placeholder="Email or Mobile"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                required
                            />
                            <button type="submit" style={styles.searchButton} disabled={loading}>
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                        </form>

                        <div style={styles.listContainer}>
                            {bookings.map((b) => (
                                <div
                                    key={b.ROWID}
                                    style={styles.listCard}
                                    onClick={() => setActiveBookingId(b.ROWID)}
                                >
                                    <div style={styles.cardHeader}>
                                        <span style={styles.eventBadge}>{b.eventType}</span>
                                        <span style={getStatusStyle(b.status)}>{b.status || 'Unknown'}</span>
                                    </div>
                                    <div style={styles.cardBody}>
                                        <div style={styles.name}>{b.customer_name}</div>
                                        <div style={styles.dateLabel}>
                                            📅 {new Date(b.booking_date).toLocaleDateString('en-GB')}
                                        </div>
                                    </div>
                                    <div style={styles.viewLink}>View Full Details →</div>
                                </div>
                            ))}

                            {searched && bookings.length === 0 && !loading && (
                                <div style={styles.noResults}>
                                    No bookings found for this contact info.
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <RenderDetails id={activeBookingId} onBack={() => setActiveBookingId(null)} />
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        backgroundColor: '#F8F5F9',
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    centeredPage: {
        backgroundColor: '#F8F5F9',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    },
    container: { width: '100%', maxWidth: '450px' },

    // Logo Alignment
    logoWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '24px',
        width: '100%'
    },
    logo: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        border: '3px solid #72398E',
        objectFit: 'cover',
        boxShadow: '0 4px 12px rgba(114, 57, 142, 0.2)'
    },

    header: { textAlign: 'center', marginBottom: '24px' },
    title: { color: '#72398E', fontSize: '24px', fontWeight: '800', margin: '0 0 8px' },
    subtitle: { color: '#718096', fontSize: '15px' },

    searchBox: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' },
    input: {
        width: '100%', padding: '15px', borderRadius: '12px',
        border: '2px solid #E2E8F0', outline: 'none', fontSize: '16px', boxSizing: 'border-box'
    },
    searchButton: {
        width: '100%', padding: '15px', backgroundColor: '#72398E',
        color: '#FFF', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '16px'
    },

    listContainer: { display: 'flex', flexDirection: 'column', gap: '16px' },
    listCard: {
        backgroundColor: '#FFF', padding: '20px', borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)', cursor: 'pointer', border: '1px solid #F0E6F5'
    },
    cardHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' },
    eventBadge: { color: '#72398E', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase' },
    cardBody: { marginBottom: '10px' },
    name: { fontSize: '17px', fontWeight: '700', color: '#2D3748' },
    dateLabel: { color: '#718096', fontSize: '13px', marginTop: '4px' },
    viewLink: { color: '#72398E', fontSize: '13px', fontWeight: '700', textAlign: 'right', borderTop: '1px solid #F7FAFC', paddingTop: '10px' },

    // Details Specific
    backBtn: { background: 'none', border: 'none', color: '#72398E', fontWeight: '700', cursor: 'pointer', marginBottom: '20px', display: 'block' },
    card: { backgroundColor: '#FFF', borderRadius: '24px', padding: '30px', border: '1px solid #F0E6F5', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' },
    detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
    detailTitle: { fontSize: '20px', fontWeight: '800', color: '#2D3748', margin: 0 },
    idLabel: { fontSize: '10px', color: '#A0AEC0', marginTop: '4px' },
    divider: { height: '1px', backgroundColor: '#EDF2F7', margin: '20px 0' },
    section: { marginBottom: '20px' },
    miniLabel: { fontSize: '10px', fontWeight: '800', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' },
    infoValue: { fontSize: '16px', fontWeight: '700', color: '#2D3748' },
    subText: { fontSize: '14px', color: '#718096' },
    infoGrid: { display: 'flex', justifyContent: 'space-between', marginBottom: '20px' },
    timeSlotBox: { backgroundColor: '#F9F5FB', padding: '16px', borderRadius: '16px', border: '1px solid #F0E6F5', marginBottom: '25px' },
    timeValue: { fontSize: '18px', fontWeight: '800', color: '#72398E' },
    footer: { textAlign: 'center' },
    footerNote: { fontSize: '12px', color: '#CBD5E0', marginBottom: '15px' },
    printButton: { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', background: 'none', color: '#718096', fontWeight: '600', cursor: 'pointer' },

    // Utility
    noResults: { textAlign: 'center', color: '#718096', padding: '20px', backgroundColor: '#FFF', borderRadius: '12px' },
    centerContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0' },
    loader: { width: '35px', height: '35px', border: '4px solid #F3EBF7', borderTop: '4px solid #72398E', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    primaryButton: { padding: '10px 20px', backgroundColor: '#72398E', color: '#FFF', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', marginTop: '15px' }
};

// Injection for loader animation
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    document.head.appendChild(styleSheet);
}

export default BookingTracker;